/**
 * A key becomes a picture, in one place.
 *
 * `avatar_key` carries two different things — `face:07`, meaning slot seven of
 * whatever style the organisation is on, and a bare media filename, meaning an
 * uploaded portrait. Telling those apart in more than one module is how you end
 * up with a broken image in every room at once, so this is the only module
 * allowed to do it. Same discipline as `format_message` being the only message
 * renderer.
 */
import { patternFace, FACE_SLOT_COUNT, type FaceAppearance } from './shape'

/**
 * The pools, in the order a person is offered them.
 *
 * `historical` leads because it is what a new organisation starts on, and
 * `patterns` trails because it is not a pool of faces at all: it is six CSS
 * patterns, and its real job is to be the render-time floor below. The three
 * drawn pools sit together at the front, ahead of the three procedural ones.
 */
export const FACE_STYLES = [
  'historical', 'animals', 'storybook', 'notionists', 'open-peeps', 'lorelei',
  'patterns',
] as const
export type FaceStyle = (typeof FACE_STYLES)[number]


/**
 * What to draw when the organisation's style is missing or unrecognised.
 *
 * Deliberately *not* the style a new organisation starts on. The server owns
 * that one. This is the render-time floor: it is drawn in
 * CSS and needs no vendored assets, so it is the one value that cannot come
 * out as a broken image whatever state the build is in. Those two really are
 * different questions, and giving them one name is how a fallback quietly
 * becomes a product decision.
 */
export const FALLBACK_FACE_STYLE: FaceStyle = 'patterns'

export type FaceArt =
  | { kind: 'pattern'; appearance: FaceAppearance; figure?: string }
  | { kind: 'image'; src: string; figure?: string }

/**
 * The cast. One name per slot, shared by every style.
 *
 * A slot is the thing that lasts: an agent wears `face:07`, and an
 * organisation changing its style re-draws that agent without touching the
 * key. So the name belongs to the slot rather than to the drawing, and an
 * agent keeps who it is when the pool around it changes.
 *
 * **Slot n's initial is the nth letter of the alphabet**, wrapping after Z, so
 * slot 10 is a J and slot 36 is a J again. That is a real ordering rather than
 * decoration: it is how you find a slot from a name, and a name from a slot,
 * without consulting this list.
 */
export const SLOT_NAMES: readonly string[] = [
  'Alva', 'Bo', 'Cato', 'Dara', 'Edda', 'Faye', 'Gil', 'Hana', 'Idris',
  'Juno', 'Kai', 'Lena', 'Mika', 'Nils', 'Otto', 'Pia', 'Quinn', 'Rune',
  'Saga', 'Tove', 'Ulla', 'Vera', 'Wim', 'Xan', 'Yara', 'Zeno', 'Anouk',
  'Bram', 'Cass', 'Dag', 'Elif', 'Freja', 'Gio', 'Hugo', 'Ines', 'Jonas',
]

/**
 * The pools whose slots are somebody in particular, which overrides the cast.
 *
 * The three drawn pools have them, because all three draw things that already
 * have names: `historical` draws people, `animals` draws a fox, and
 * `storybook` draws a fox who is a fiddler. Order is the file order in
 * `web/public/faces/<style>/`, and a test reads the `<title>` out of each of
 * those files and asserts it matches these lines one for one, so a name
 * cannot drift from the drawing it belongs to.
 *
 * Nothing depends on these being shown. Every figure is drawn to be recognised
 * without a caption; the name is what a screen reader gets, what a tooltip
 * says, and what tells you which face you are about to take.
 */
export const FIGURE_NAMES: Partial<Record<FaceStyle, readonly string[]>> = {
  historical: [
    'Winston Churchill', 'Albert Einstein', 'Cleopatra', 'Napoleon Bonaparte',
    'Frida Kahlo', 'Florence Nightingale', 'Abraham Lincoln', 'Marie Curie',
    'Salvador Dali', 'William Shakespeare', 'Julius Caesar', 'Elvis Presley',
    'Mahatma Gandhi', 'Marilyn Monroe', 'Groucho Marx', 'Pablo Picasso',
    'Bob Marley', 'Vincent van Gogh', 'Genghis Khan', 'Bruce Lee',
    'Freddie Mercury', 'Steve Jobs', 'Mother Teresa', 'Muhammad Ali',
    'Amelia Earhart', 'Jimi Hendrix', 'Audrey Hepburn', 'Che Guevara',
    'Sigmund Freud', 'John Lennon', 'Queen Elizabeth II', 'Princess Diana',
    'Fidel Castro', 'Elton John', 'Louis Armstrong', 'Charlie Chaplin',
  ],
  // Like the cast, slot n's initial is the alphabet's nth letter, wrapping
  // after Z — so slot 26 is a zebra and slot 27 is an axolotl. It is the same
  // rule for the same reason: a name finds its slot, and a slot its name,
  // without anybody reading the list.
  animals: [
    'Alpaca', 'Bear', 'Cat', 'Deer', 'Elephant', 'Fox', 'Giraffe', 'Hedgehog',
    'Ibex', 'Jaguar', 'Koala', 'Lion', 'Moose', 'Narwhal', 'Owl', 'Panda',
    'Quail', 'Rabbit', 'Squirrel', 'Tiger', 'Uakari', 'Vulture', 'Wolf',
    'Xolo Dog', 'Yak', 'Zebra', 'Axolotl', 'Badger', 'Chameleon', 'Duck',
    'Eagle', 'Frog', 'Gorilla', 'Hippo', 'Iguana', 'Jay',
  ],
  // Same alphabet rule again, on the character's first name. Nobody here is
  // anybody else's character: the pool is drawn in the idiom of golden-age
  // animation, which is not owned by anybody, rather than from the films,
  // which are.
  storybook: [
    'Alaric the Stag Prince', 'Bramble the Badger Baker', 'Clementine the Cat Fortune Teller',
    'Dorian the Mouse Magician', 'Elspeth the Hedgehog Fairy', 'Fenwick the Fox Fiddler',
    'Gwendolyn the Goose Innkeeper', 'Hollis the Hare Knight', 'Isolde the Owl Sorceress',
    'Joachim the Jackdaw Pirate', 'Konrad the Bear Woodsman', 'Linnea the Lamb Shepherdess',
    'Montgomery the Mole Tinker', 'Nadia the Nightingale Singer',
    'Oskar the Otter Sailor', 'Priscilla the Peacock Duchess', 'Quintus the Porcupine Scribe',
    'Rowan the Raccoon Bandit', 'Sorrel the Skunk Poet', 'Thaddeus the Toad Judge',
    'Ursa the Bear Empress', 'Viola the Vixen Diva', 'Wendell the Walrus Admiral',
    'Xerxes the Tortoise Sage', 'Yorick the Yak Minstrel', 'Zephyr the Zebra Acrobat',
    'Ambrose the Ape Strongman', 'Beatrix the Bunny Ballerina', 'Cormac the Crow Postman',
    'Dmitri the Duck Guardsman', 'Etta the Elephant Circus Star',
    'Fabian the Flamingo Waiter', 'Greta the Goat Alpinist', 'Hawthorne the Hound Detective',
    'Ivo the Ibex Monk', 'Juniper the Jackal Explorer',
  ],
}

/**
 * Who is in a slot. Every slot of every style is somebody.
 *
 * A slot outside the pool wraps rather than coming back empty, for the same
 * reason `patternFace` wraps: a name is cosmetic, and a face with no name is a
 * worse answer than a face with the wrong one.
 */
export function figureFor(style: FaceStyle, slot: number): string {
  const index = (((Math.trunc(slot) - 1) % FACE_SLOT_COUNT) + FACE_SLOT_COUNT) % FACE_SLOT_COUNT
  return FIGURE_NAMES[style]?.[index] ?? SLOT_NAMES[index]
}

const SLOT = /^face:(\d{1,2})$/

/** The slot a `face:NN` key names, or null for no key or an uploaded portrait's media key. */
export function faceSlot(avatarKey: string | null | undefined): number | null {
  const slotMatch = avatarKey ? SLOT.exec(avatarKey) : null
  return slotMatch ? Number(slotMatch[1]) : null
}

/** Whether a string names a style that actually exists. */
export function isFaceStyle(value: string | null | undefined): value is FaceStyle {
  return FACE_STYLES.includes(value as FaceStyle)
}

/**
 * FNV-1a over the name's UTF-8 bytes.
 *
 * **This must stay identical to `_stable_hash` in the server's
 * `services.py`**, which is what makes `preferredFaceSlot` below agree with
 * the slot the server reserves. Note the two differences from the `hash` in
 * `shape.ts`, which answers a cosmetic question and so is free to be sloppy:
 * bytes rather than UTF-16 code units, and an unsigned 32-bit result rather
 * than `Math.abs` of a signed one.
 */
export function stableHash(value: string): number {
  let result = 0x811c9dc5
  for (const byte of new TextEncoder().encode(value)) {
    result ^= byte
    result = Math.imul(result, 0x01000193) >>> 0
  }
  return result
}

/**
 * The slot an agent of this name gets when none is reserved for it.
 *
 * An organisation has thirty-six faces and no limit on agents, so past the
 * thirty-sixth the server has nothing left to reserve and stores a null key.
 * Those agents still need to look like they belong to the same cast as
 * everyone else, so a null key means "whatever my name hashes to, and I do not
 * mind sharing it" rather than "nobody".
 *
 * The hash is the server's, not `shape.ts`'s, and deliberately: this is the
 * first slot `services.candidate_face_slots` offers the same name, so an agent
 * drawn as Cleopatra today is *given* Cleopatra the moment a slot frees, and
 * its face does not jump when that happens.
 */
export function preferredFaceSlot(name: string): number {
  return (stableHash(name) % FACE_SLOT_COUNT) + 1
}

/**
 * One slot of one style, drawn.
 *
 * Both ways into a slot come through here — a reserved key and the name-hashed
 * fallback — so the two cannot drift into looking different. `patterns` is the
 * only style with no files behind it; it is drawn in CSS, which is what makes
 * it the render-time floor.
 */
function slotArt(style: FaceStyle, slot: number,
                 options: { online?: boolean; ring?: string }): FaceArt {
  const figure = figureFor(style, slot)
  if (style === 'patterns') {
    return { kind: 'pattern', appearance: patternFace(slot, options), figure }
  }
  return { kind: 'image', src: `/faces/${style}/${String(slot).padStart(2, '0')}.svg`, figure }
}

/**
 * An organisation's own pool, expanded, the way it reaches the browser on
 * `OrganisationOut`. Sparse: a set may fill eight of the thirty-six slots.
 */
export interface FaceSetView {
  slug: string
  fallbackStyle: FaceStyle
  /** Slot number to what to draw. Sparse: a set may fill eight of thirty-six. */
  slots: Record<number, { url: string; name: string }>
}

const SET = /^set:(.+)$/

/**
 * What to draw for one agent.
 *
 * `online` and `ring` reach every kind of face. They have to: an agent is
 * given a slot the moment it turns up, so a presence rule that only applied to
 * the placeholder would apply to almost nobody.
 *
 * `style` is a plain string, not `FaceStyle`, because an organisation on its
 * own pool carries `set:<slug>`, which is not one of the five built-ins. `set`
 * is that pool, expanded, or null on a page with no organisation behind it.
 * That is the marketing site, which passes neither and lands on the floor
 * below.
 */
export function faceArt(
  name: string,
  avatarKey: string | null | undefined,
  style: string,
  options: { online?: boolean; ring?: string } = {},
  set: FaceSetView | null = null,
): FaceArt {
  const named = SET.exec(style)
  const slotMatch = avatarKey ? SLOT.exec(avatarKey) : null

  // A key that does not match SLOT is a media key, never a slot number, so it
  // is excluded from the pool lookup below rather than coerced into slot
  // zero. When that happens the condition below is false and execution falls
  // through to the portrait branch at the bottom, the same one a media key
  // reaches under a built-in style.
  if (named && (!avatarKey || slotMatch)) {
    // A page with no organisation behind it has no set to draw, so it goes to
    // the floor rather than to a broken image. That is the marketing site.
    if (!set || set.slug !== named[1]) {
      return faceArt(name, avatarKey, FALLBACK_FACE_STYLE, options)
    }
    const slot = slotMatch ? Number(slotMatch[1]) : preferredFaceSlot(name)
    const filled = set.slots[slot]
    if (filled) {
      return { kind: 'image', src: filled.url, figure: filled.name }
    }
    // One level deep by construction: a fallback is always a built-in,
    // enforced by _check_fallback_style on the server.
    return faceArt(name, avatarKey, set.fallbackStyle, options)
  }

  // A style the browser does not recognise, whether a stale built-in or a
  // set already handled above, resolves to the floor here rather than being
  // cast. Casting would build a path with no asset behind it the moment the
  // server ships a style the bundle does not know how to draw yet.
  const resolvedStyle: FaceStyle = isFaceStyle(style) ? style : FALLBACK_FACE_STYLE

  if (!avatarKey) {
    return slotArt(resolvedStyle, preferredFaceSlot(name), options)
  }

  if (slotMatch) {
    return slotArt(resolvedStyle, Number(slotMatch[1]), options)
  }

  // An uploaded portrait is nobody from the pool and wins over every style,
  // set or built-in. It stays last because a media key can never be a slot.
  return { kind: 'image', src: `/media/${avatarKey}` }
}
